const EXTENSIONS = [".md", ".markdown"];

/**
 * A file name that is safe to create in a flat folder.
 * @param {string} name
 */
export function isSafeName(name: string) {
  if (typeof name !== "string") return false;
  if (name.length === 0 || name.length > 200) return false;
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are exactly what a file name must not contain
  if (/[\\/:*?"<>|\u0000-\u001f]/.test(name)) return false;
  if (name.startsWith(".") || name.endsWith(" ") || name.endsWith(".")) return false;
  return isMarkdownName(name);
}

/** @param {string} name */
export function isMarkdownName(name: string) {
  const lower = name.toLowerCase();
  return EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Turns a title into a file stem: lower case, accents removed, dashes between words.
 * @param {string} title
 */
export function slugify(title: string) {
  const slug = String(title)
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
  return slug || "untitled";
}

/**
 * Adds .md when no Markdown extension is given.
 * @param {string} name
 */
export function withExtension(name: string) {
  const trimmed = String(name).trim();
  return isMarkdownName(trimmed) ? trimmed : `${trimmed}.md`;
}

/**
 * A name that does not clash with existing ones: note.md, note-2.md, note-3.md.
 * @param {string} base file name with extension
 * @param {Iterable<string>} existing
 */
export function uniqueName(base: string, existing: Iterable<string>) {
  const taken = new Set([...existing].map((n) => n.toLowerCase()));
  if (!taken.has(base.toLowerCase())) return base;
  const dot = base.lastIndexOf(".");
  const stem = dot === -1 ? base : base.slice(0, dot);
  const ext = dot === -1 ? "" : base.slice(dot);
  for (let i = 2; ; i += 1) {
    const candidate = `${stem}-${i}${ext}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * Why a file cannot take this new name, or null when it can.
 * @param {string} name the new name, with extension
 * @param {string} current the file's present name
 * @param {Iterable<string>} existing every file name in the library
 */
export function renameProblem(name: string, current: string, existing: Iterable<string>) {
  if (!isSafeName(name)) return "Use letters, numbers, spaces, dashes and underscores.";
  const lower = name.toLowerCase();
  for (const other of existing) {
    if (other.toLowerCase() === lower && other.toLowerCase() !== current.toLowerCase()) {
      return "A file with that name already exists.";
    }
  }
  return null;
}
